import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, Group, Stack, Text, Title } from '@mantine/core';
import {
  IconBuildingStore,
  IconChevronRight,
  IconRobot,
  IconRoute,
  IconSparkles,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';

/**
 * Settings overview. Routes vendors into the typed sub-pages
 * (general / ai-and-timing / routing). The raw key/value editor was
 * intentionally removed — every legitimate vendor-side setting now has a
 * typed UI, and SuperAdmin retains the raw editor at /central/configuration
 * for support escapes.
 */
function SettingsIndex() {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Title order={2}>Settings</Title>
        <Text c="dimmed" size="sm">
          Pick a category below.
        </Text>

        <Stack gap="xs">
          <SettingsLink
            to="/vendor/settings/general"
            icon={<IconBuildingStore size={18} />}
            title="General"
            description="Business name, contact, address, timezone, default language."
          />
          <SettingsLink
            to="/vendor/settings/ai-agents"
            icon={<IconRobot size={18} />}
            title="AI agents"
            description="Create AI agents with your business context + knowledge files that auto-reply to customers."
          />
          <SettingsLink
            to="/vendor/settings/ai-and-timing"
            icon={<IconSparkles size={18} />}
            title="AI & timing"
            description="Provider (Gemini / OpenAI), API keys, business hours when the bot replies."
          />
          <SettingsLink
            to="/vendor/settings/routing"
            icon={<IconRoute size={18} />}
            title="Team assignment"
            description="Auto-assign inbound conversations to agents by channel, label, or keyword."
          />
        </Stack>
      </Stack>
    </motion.div>
  );
}

function SettingsLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card component={Link} to={to} withBorder style={{ textDecoration: 'none' }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md">
          {icon}
          <Stack gap={2}>
            <Text fw={600}>{title}</Text>
            <Text c="dimmed" size="sm">
              {description}
            </Text>
          </Stack>
        </Group>
        <IconChevronRight size={18} />
      </Group>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/settings/')({ component: SettingsIndex });
